/**
 * S3-served invoice/credit-note PDF opening (AB#20717 round 2 demo-feedback fix #2, tightened in
 * round 3 — see the "No Stripe redirect from the PDF column" fix below).
 *
 * The backend stores a signed S3 copy of Stripe invoice PDFs and credit-note PDFs, keyed by
 * `invoice_pdf_s3_key` / `credit_note_pdf_s3_key`, and now FETCHES ON DEMAND when a row is opened
 * before a copy has been stored yet — a miss on the first click usually still yields a URL after a
 * ~1-2s backend round trip, so the endpoint is always worth calling rather than pre-checking for a
 * key client-side.
 *
 * Round 3 contract change: these handlers no longer fall back to any Stripe-hosted link
 * (`resolveInvoiceDocumentUrl`'s ladder for invoices, `credit.pdf` for credit notes) when the
 * endpoint returns a null url or the request fails. That silent fallback was the source of the
 * "ghost redirect" bug family (order #26529) — it could resurrect a stale/expired Stripe link for
 * a document that no longer matches the order's current billing state. Callers get back a plain
 * status instead and decide how to render "not available yet" themselves (disabled link, toast,
 * etc. — see `BillingDocumentsLedger.vue` / `PriceBreakdownModal.vue`).
 *
 * Both handlers still guard against double-clicks with a simple in-flight id set — clicking the
 * same row again while its request is outstanding is a no-op (resolves to `'unavailable'` without
 * firing a second request) rather than firing a second `window.open`.
 */
import { http } from '@/interceptors/axiosRequestInterceptor';

export type PdfOpenResult = 'opened' | 'unavailable';

function openUrl(url: string | null | undefined): boolean {
  if (typeof url !== 'string' || url.trim() === '') return false;
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
}

// ---------------------------------------------------------------------------
// Invoice PDFs — GET order-item-invoices/{id}/pdf-url
// ---------------------------------------------------------------------------

interface InvoicePdfUrlResult {
  url: string | null;
}

const invoiceInFlight = new Set<string>();

/**
 * Click handler for an invoice's PDF link. Always resolves via the `pdf-url` endpoint (never a
 * Stripe-hosted fallback — see module doc). Resolves to `'unavailable'` without ever calling
 * `window.open` when the row has no usable numeric id, a request is already in flight for it, the
 * endpoint returns a null url, or the request throws.
 */
export async function openInvoicePdf(
  inv: Record<string, unknown> | null | undefined,
): Promise<PdfOpenResult> {
  const rec = inv ?? {};
  const idNum = Number(rec.id);
  if (!Number.isFinite(idNum) || idNum <= 0) return 'unavailable';

  const guardKey = String(idNum);
  if (invoiceInFlight.has(guardKey)) return 'unavailable';
  invoiceInFlight.add(guardKey);
  try {
    const response = await http.get<{ message: string; result: InvoicePdfUrlResult }>(
      `order-item-invoices/${idNum}/pdf-url`,
    );
    return openUrl(response.data?.result?.url) ? 'opened' : 'unavailable';
  } catch {
    return 'unavailable';
  } finally {
    invoiceInFlight.delete(guardKey);
  }
}

// ---------------------------------------------------------------------------
// Credit-note PDFs — GET order-customer-credits/{id}/pdf-url
// ---------------------------------------------------------------------------

interface CreditNotePdfUrlResult {
  url: string | null;
}

const creditNoteInFlight = new Set<string>();

/**
 * Click handler for a credit note's PDF link. `credit` accepts the embedded
 * `order_item_invoices.credit_notes[]` payload entry (numeric `id`, endpoint-eligible) — rows
 * sourced purely from a Stripe credit-note-eligibility summary (`CreditNoteExistingCnSummary`,
 * whose `id` is the Stripe credit-note id, e.g. `cn_xxx`, not the DB row id) can't be resolved
 * through this endpoint at all and always settle to `'unavailable'`.
 *
 * Always resolves via the `pdf-url` endpoint when a numeric id is present (never a Stripe-hosted
 * fallback — see module doc).
 */
export async function openCreditNotePdf(
  credit: Record<string, unknown> | null | undefined,
): Promise<PdfOpenResult> {
  const rec = credit ?? {};
  const idNum = Number(rec.id);
  if (!Number.isFinite(idNum) || idNum <= 0) return 'unavailable';

  const guardKey = String(idNum);
  if (creditNoteInFlight.has(guardKey)) return 'unavailable';
  creditNoteInFlight.add(guardKey);
  try {
    const response = await http.get<{ message: string; result: CreditNotePdfUrlResult }>(
      `order-customer-credits/${idNum}/pdf-url`,
    );
    return openUrl(response.data?.result?.url) ? 'opened' : 'unavailable';
  } catch {
    return 'unavailable';
  } finally {
    creditNoteInFlight.delete(guardKey);
  }
}
